import { Component, Inject, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-nonedittextbox',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <!-- Simple Question Text View -->
    @if (isSimpleView) {
      <div class="simple-text-container">
        <div class="modal-header">
          <h2 class="modal-title">{{ data.title }}</h2>
          <button
            mat-icon-button
            class="close-icon-btn"
            (click)="closeDialog()"
            aria-label="Close"
            >
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="modal-content">
          <div class="question-text-section" [innerHTML]="safeText"></div>
        </div>
      </div>
    }
    
    <!-- Full Question Details View -->
    @if (!isSimpleView) {
      <div class="question-details-container">
        <div class="modal-header">
          <h2 class="modal-title">{{ data.title }}</h2>
          <button
            mat-icon-button
            class="close-icon-btn"
            (click)="closeDialog()"
            aria-label="Close"
            >
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="modal-content">
          <!-- LANGUAGE TOGGLE SECTION WITH SLIDER -->
          @if (relatedQuestions && relatedQuestions.length > 1) {
            <div class="language-toggle-section">
              <!-- Language Tabs Slider -->
              <div class="language-tabs-wrapper" #languageTabsWrapper>
                <div class="language-tabs">
                  @for (question of relatedQuestions; track trackByQuestionId($index, question)) {
                    <div
                      class="language-tab"
                      [class.active]="currentLanguageId === question.question_id"
                      (click)="switchLanguage(question.question_id)"
                      >
                      {{ question.language_name }}
                    </div>
                  }
                </div>
              </div>
              <div class="divider"></div>
            </div>
          }
          <div class="details-section">
            <div class="section-header">
              <div class="section-title">Basic Information</div>
            </div>
            <div class="details-grid">
              @for (item of basicInfo; track item) {
                <div class="detail-row">
                  <div class="detail-label">{{item.label}}</div>
                  <div class="detail-value" [innerHTML]="item.safeValue"></div>
                </div>
              }
            </div>
          </div>
          @if (courseInfo.length) {
            <div class="details-section">
              <div class="section-header">
                <div class="section-title">Course & Version</div>
              </div>
              <div class="details-grid">
                @for (item of courseInfo; track item) {
                  <div class="detail-row">
                    <div class="detail-label">{{item.label}}</div>
                    <div class="detail-value" [innerHTML]="item.safeValue"></div>
                  </div>
                }
              </div>
            </div>
          }
          @if (questionSpecificInfo.length) {
            <div class="details-section">
              <div class="section-header">
                <div class="section-title">Question Specific Details</div>
              </div>
              <div class="details-grid">
                @for (item of questionSpecificInfo; track item) {
                  <!-- Special handling for Matching Table -->
                  @if (item.key === 'Matching Table') {
                    <div class="matching-table-container">
                      <div class="matching-table" [innerHTML]="item.safeValue"></div>
                    </div>
                  }
                  <!-- Regular detail rows for other items -->
                  @if (item.key !== 'Matching Table') {
                    <div class="detail-row" [class.full-width]="isFullWidth(item.key)">
                      <div class="detail-label">{{item.label}}</div>
                      <div class="detail-value" [innerHTML]="item.safeValue"></div>
                    </div>
                  }
                }
              </div>
            </div>
          }
          @if (additionalInfo.length) {
            <div class="details-section">
              <div class="section-header">
                <div class="section-title">Additional Information</div>
              </div>
              <div class="details-grid">
                @for (item of additionalInfo; track item) {
                  <div class="detail-row" [ngClass]="{'full-width': isFullWidth(item.label)}">
                    <div class="detail-label">{{item.label}}</div>
                    <div class="detail-value" [innerHTML]="item.safeValue"></div>
                  </div>
                }
              </div>
            </div>
          }
          @if (filesInfo.length) {
            <div class="details-section">
              <div class="section-header">
                <div class="section-title">Files & Metadata</div>
              </div>
              <div class="details-grid">
                @for (item of filesInfo; track item) {
                  <div class="detail-row" [class.full-width]="item.isLabels">
                    <div class="detail-label">{{item.label}}</div>
                    <!-- Collapsible Labels Tree View - Hierarchical Structure -->
                    @if (item.isLabels) {
                      <div class="labels-tree-container">
                        @for (labelGroup of item.labelsData; track labelGroup) {
                          @if (labelGroup.labels && labelGroup.labels.length > 0) {
                            <div class="label-group">
                              <!-- Render root level labels (level 0) -->
                              @for (label of labelGroup.labels; track label) {
                                @if ((label.level || 0) === 0) {
                                  <ng-container *ngTemplateOutlet="labelTreeNode; context: { $implicit: label, labels: labelGroup.labels }"></ng-container>
                                }
                              }
                            </div>
                          }
                        }
                      </div>
                    }
                    <!-- Recursive Tree Node Template -->
                    <ng-template #labelTreeNode let-label let-allLabels="labels">
                      <div class="label-node">
                        <!-- Label Header with Toggle Icon -->
                        <div class="label-header" (click)="toggleLabelExpand(label.labelId)" [title]="getFullLabelTitle(label)">
                          <!-- Toggle Icon - Show only if has children -->
                          @if (hasChildren(label, allLabels)) {
                            <span class="toggle-icon" [class.expanded]="expandedLabels[label.labelId]">
                              ▶
                            </span>
                          }
                          @if (!hasChildren(label, allLabels)) {
                            <span class="toggle-icon-placeholder"></span>
                          }
                          <!-- Display Original Label Name (NOT the API matched name) -->
                          <span class="label-text">{{ label.labelName }}</span>
                        </div>
                        <!-- Children Container -->
                        @if (expandedLabels[label.labelId] && hasChildren(label, allLabels)) {
                          <div class="children-container">
                            @for (childLabel of getDirectChildren(label, allLabels); track childLabel) {
                              <ng-container *ngTemplateOutlet="labelTreeNode; context: { $implicit: childLabel, labels: allLabels }"></ng-container>
                            }
                          </div>
                        }
                      </div>
                    </ng-template>
                    <!-- Regular display for non-label items -->
                    @if (!item.isLabels) {
                      <div class="detail-value" [innerHTML]="item.safeValue"></div>
                    }
                  </div>
                }
              </div>
            </div>
          }
          @if (statusInfo.length) {
            <div class="details-section">
              <div class="section-header">
                <div class="section-title">Status & Audit</div>
              </div>
              <div class="details-grid">
                @for (item of statusInfo; track item) {
                  <div class="detail-row">
                    <div class="detail-label">{{item.label}}</div>
                    <div class="detail-value" [innerHTML]="item.safeValue"></div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    }
    `,
    styles: [`
    /* Simple Text View Styles */
    .simple-text-container {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: white;
      border-radius: 8px;
      overflow: hidden;
      max-width: 900px;
      max-height: 85vh;
      border: 1px solid transparent;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
    }

    .simple-text-container .modal-header {
      background-color: #E3E3E6;
      color: #333333;
      padding: 16px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #ddd;
    }

    .simple-text-container .modal-content {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
    }

    .question-text-section {
      font-size: 15px;
      line-height: 1.8;
      color: #333333;
      word-break: break-word;
      white-space: pre-wrap;
    }

    .question-text-section p {
      margin: 0 0 12px 0;
    }

    .question-text-section table {
      border-collapse: collapse;
      width: 100%;
      margin: 12px 0;
    }

    .question-text-section td, .question-text-section th {
      border: 1px solid #ddd;
      padding: 8px;
    }

    .question-text-section th {
      background-color: #f5f5f5;
      font-weight: 600;
    }

    /* Scrollbar styling for simple view */
    .simple-text-container .modal-content {
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f5f5f5;
    }

    .simple-text-container .modal-content::-webkit-scrollbar {
      width: 6px;
    }

    .simple-text-container .modal-content::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 3px;
    }

    .simple-text-container .modal-content::-webkit-scrollbar-thumb {
      background-color: #c1c1c1;
      border-radius: 3px;
    }

    .simple-text-container .modal-content::-webkit-scrollbar-thumb:hover {
      background-color: #a8a8a8;
    }

    .question-details-container {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: white;
      border-radius: 8px;
      overflow: hidden;
      width: 100%;
      max-width: 900px;
      min-width: 900px;
      max-height: 80vh !important;
      border: 1px solid transparent;
      margin: 0 auto;
    }
    
    ::ng-deep .question-details-modal .mat-dialog-container {
      width: 900px !important;
      max-width: 900px !important;
      min-width: 900px !important;
    }

    .modal-header {
      background-color: #E3E3E6;
      color: #333333;
      padding: 16px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid trasparent;
    }
    
    .modal-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      color: #333333;
      letter-spacing: 0.3px;
    }
    
    .close-icon-btn {
      color: #666666;
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: background-color 0.2s;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .close-icon-btn:hover {
      background-color: rgba(0, 0, 0, 0.05);
      color: #333333;
    }
    
    .close-icon-btn mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
    
    .modal-content {
      padding: 0;
      max-height: 60vh;
      overflow-y: auto;
      border: none !important;
    }
    
    /* LANGUAGE TOGGLE STYLES WITH SLIDER */
    .language-toggle-section {
      background: linear-gradient(135deg, #f8f9fa 0%, #f0f1f3 100%);
      padding: 2px 0;
      margin-top:12px;
      border-bottom: 1px solid #e0e0e0;
      margin-bottom: 0;
    }
    
    .language-tabs-wrapper {
      overflow-x: auto;
      overflow-y: hidden;
      scroll-behavior: smooth;
      padding: 8px 24px;
    }
    
    /* Custom Scrollbar */
    .language-tabs-wrapper::-webkit-scrollbar {
      height: 6px;
    }
    
    .language-tabs-wrapper::-webkit-scrollbar-track {
      background: #f0f0f0;
      border-radius: 10px;
    }
    
    .language-tabs-wrapper::-webkit-scrollbar-thumb {
      background: #28a745;
      border-radius: 10px;
      transition: background 0.3s ease;
    }
    
    .language-tabs-wrapper::-webkit-scrollbar-thumb:hover {
      background: #1e8449;
    }
    
    /* Firefox Scrollbar */
    .language-tabs-wrapper {
      scrollbar-color: #28a745 #f0f0f0;
      scrollbar-width: thin;
    }
    
    .language-tabs {
      display: flex;
      gap: 8px;
      white-space: nowrap;
      padding: 0;
      margin: 0;
    }
    
    .language-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
    }
    
    .language-label {
      font-weight: 700;
      color: #1a1a1a;
      font-size: 15px;
      letter-spacing: 0.3px;
    }
    
    .language-tab {
      display: inline-block;
      padding: 12px 16px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      color: #666;
      white-space: nowrap;
      flex-shrink: 0;
      border-bottom: 3px solid transparent;
      transition: color 0.2s ease, border-color 0.2s ease;
      background: transparent;
      border: none;
    }
    
    .language-tab:hover {
      color: #28a745;
    }
    
    .language-tab.active {
      color: #28a745;
      border-bottom-color: #28a745;
      font-weight: 700;
    }
    
    .language-flag {
      font-size: 18px;
    }
    
    .language-name {
      font-weight: 700;
      font-size: 14px;
    }
    
    .question-id {
      font-size: 12px;
      opacity: 0.7;
      font-weight: 500;
    }
    
    .language-tab.active .question-id {
      opacity: 0.9;
      color: #28a745;
    }
    
    .divider {
      display: none;
    }
    
    .details-section {
      border-bottom: 1px solid transparent;
      padding: 20px 24px;
    }
    
    .details-section:last-child {
      border-bottom: none;
    }
    
    .section-header {
      margin-bottom: 16px;
    }
    
    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #333333;
      padding-bottom: 8px;
      border-bottom: 1px solid #e0e0e0;
      display: inline-block;
      width: 100%;
    }
    
    .details-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 12px;
    }
    
    .detail-row {
      display: grid;
      grid-template-columns: 200px 1fr;
      gap: 16px;
      padding: 8px 0;
      min-height: 24px;
      align-items: start;
    }
    
    .detail-row.full-width {
      grid-column: 1 / -1;
      display: block;
    }
    
    .detail-label {
      font-weight: 500;
      color: #666666;
      font-size: 14px;
      line-height: 1.5;
      word-break: break-word;
    }
    
    .detail-value {
      color: #333333;
      font-size: 14px;
      line-height: 1.5;
      word-break: break-word;
    }
    
    /* Matching Table Styles */
    .matching-table-container {
      grid-column: 1 / -1;
      margin: 10px 0;
    }
    
    .matching-table h4 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 14px;
      font-weight: 600;
    }
    
    .matching-table > div {
      margin-bottom: 20px;
    }
    
    .matching-table .flex-container {
      display: flex;
      gap: 30px;
      margin-bottom: 20px;
    }
    
    .matching-table .column {
      flex: 1;
    }
    
    .matching-table .list-box {
      border: 1px solid #ddd;
      border-radius: 4px;
      overflow: hidden;
    }
    
    .matching-table .list-item {
      padding: 8px 12px;
      border-bottom: 1px solid #eee;
      background-color: #f8f9fa;
    }
    
    .matching-table .list-item:last-child {
      border-bottom: none;
    }
    
    .matching-table .list-subitem {
      padding: 4px 0 4px 20px;
    }
    
    .matching-table .sequence-container {
      background-color: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 4px;
      padding: 12px;
    }
    
    .matching-table .match-item {
      padding: 6px 10px;
      margin-bottom: 4px;
      background-color: white;
      border-radius: 3px;
      border-left: 3px solid #28a745;
    }
    
    /* Links */
    .detail-value a {
      color: #0066cc;
      text-decoration: none;
      background: none;
      padding: 0;
      margin: 0;
      display: inline;
    }
    
    .detail-value a:hover {
      text-decoration: underline;
      background: none;
      color: #004499;
    }
    
    /* Collapsible Labels Tree Styles */
    .labels-tree-container {
      color: #333333;
      font-size: 14px;
      padding: 8px 0;
    }
    
    .label-group {
      margin-bottom: 8px;
    }
    
    .label-node {
      margin-bottom: 0px;
    }
    
    .label-header {
      display: flex;
      align-items: center;
      cursor: pointer;
      padding: 6px 8px;
      border-radius: 4px;
      user-select: none;
      transition: background-color 0.2s ease;
    }
    
    .label-header:hover {
      background-color: #f0f0f0;
    }
    
    .toggle-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin-right: 8px;
      color: #666666;
      font-size: 12px;
      transition: transform 0.2s ease;
      flex-shrink: 0;
    }
    
    .toggle-icon.expanded {
      transform: rotate(90deg);
    }
    
    .toggle-icon-placeholder {
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-right: 8px;
      flex-shrink: 0;
    }
    
    .label-text {
      font-weight: 500;
      color: #333333;
      word-break: break-word;
    }
    
    .children-container {
      margin-left: 24px;
      padding-left: 8px;
      border-left: 2px solid #d0d0d0;
      margin-top: 2px;
    }
    
    .label-node .label-node {
      margin-bottom: 0px;
    }
    
    .label-node .label-header {
      padding: 4px 8px;
      font-size: 13px;
    }
    
    .label-node .label-text {
      font-weight: 400;
      color: #555555;
      font-size: 13px;
    }

    /* Scrollbar styling */
    ::ng-deep .modal-content {
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f5f5f5;
    }
    
    ::ng-deep .modal-content::-webkit-scrollbar {
      width: 6px;
    }
    
    ::ng-deep .modal-content::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 3px;
    }
    
    ::ng-deep .modal-content::-webkit-scrollbar-thumb {
      background-color: #c1c1c1;
      border-radius: 3px;
    }
    
    ::ng-deep .modal-content::-webkit-scrollbar-thumb:hover {
      background-color: #a8a8a8;
    }
    
    /* Responsive design */
    @media (max-width: 768px) {
      .detail-row {
        grid-template-columns: 1fr;
        gap: 4px;
      }
      
      .detail-row.full-width {
        grid-column: auto;
      }
      
      .detail-label {
        font-weight: 600;
        color: #444;
      }
      
      .details-section {
        padding: 16px;
      }
      
      .modal-header {
        padding: 12px 16px;
      }
      
      .matching-table .flex-container {
        flex-direction: column;
        gap: 20px;
      }
    }
  `],
    standalone: false
})
export class NonedittextboxComponent implements OnInit {
  basicInfo: Array<{ key: string, label: string, safeValue: SafeHtml }> = [];
  courseInfo: Array<{ key: string, label: string, safeValue: SafeHtml }> = [];
  questionSpecificInfo: Array<{ key: string, label: string, safeValue: SafeHtml }> = [];
  additionalInfo: Array<{ key: string, label: string, safeValue: SafeHtml }> = [];
  filesInfo: Array<{ key: string, label: string, safeValue: SafeHtml, isLabels?: boolean, labelsData?: any }> = [];
  statusInfo: Array<{ key: string, label: string, safeValue: SafeHtml }> = [];
  isSimpleView: boolean = false;
  safeText: SafeHtml = '';
  expandedLabels: { [key: string]: boolean } = {};
  relatedQuestions: any[] = []; // Store related language versions
  allQuestionDetails: any = {}; // Store all details for all languages
  currentLanguageId: number = 0; // Track current language being viewed
  prepareDetailsMethod: any = null; // Reference to parent's prepareQuestionDetails method
  element: any = null; // Store raw element data
  hasUserSwitchedLanguage: boolean = false; // Track if user has manually switched language
  
  @ViewChild('languageTabsWrapper') languageTabsWrapper: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
    public cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<NonedittextboxComponent>
  ) {
    if (this.data.element) {
      this.element = this.data.element;
    }
        if (this.data.prepareDetailsMethod) {
      this.prepareDetailsMethod = this.data.prepareDetailsMethod;
    }
    
    // Store related questions if provided
    if (this.data.relatedQuestions) {
      this.relatedQuestions = this.data.relatedQuestions;
    }
    // Store all question details for all languages
    if (this.data.allQuestionDetails) {
      this.allQuestionDetails = this.data.allQuestionDetails;
    }

    // Set current language ID
    if (this.data.currentLanguageId) {
      this.currentLanguageId = this.data.currentLanguageId;
    }

    // Check if this is a simple text view (for question paper generation)
    if (this.data.text && !this.data.details) {
      this.isSimpleView = true;
      // Strip HTML tags and format the text for display
      const plainText = this.stripHtmlTags(this.data.text);
      const formattedText = plainText
        .replace(/\n/g, '<br>')
        .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
      this.safeText = this.sanitizer.bypassSecurityTrustHtml(formattedText);
    }
  }

  ngOnInit(): void {
    
    if (this.isSimpleView) {
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
      });
    } else {
      if (this.data.details) {
        // Defer categorizeDetails to ngOnInit lifecycle
        Promise.resolve().then(() => {
          this.categorizeDetails();
          this.cdr.markForCheck();
        });
      } else if (this.element && this.prepareDetailsMethod) {
        Promise.resolve().then(() => {
          this.prepareAndCategorizeDetails();
          this.cdr.markForCheck();
        });
      }
    }
  }
  updateWithFreshDetails(freshDetails: any, freshElement: any): void {
    this.element = freshElement;
    if (this.hasUserSwitchedLanguage && this.currentLanguageId !== freshElement.question_id) {
      console.log('User has switched language - preserving current language view');
      
      if (freshElement.question_id && this.allQuestionDetails[freshElement.question_id]) {
        this.allQuestionDetails[freshElement.question_id] = freshDetails;
      }
      
      return;
    }
    this.data.details = freshDetails;
    
    // Re-categorize with fresh details
    this.basicInfo = [];
    this.courseInfo = [];
    this.questionSpecificInfo = [];
    this.additionalInfo = [];
    this.filesInfo = [];
    this.statusInfo = [];
    
    this.categorizeDetails();
  }

  prepareAndCategorizeDetails() {
    if (!this.data.details && this.element && this.prepareDetailsMethod) {
      this.data.details = this.prepareDetailsMethod(this.element);
    }
    
    if (this.data.details) {
      this.categorizeDetails();
    }
  }

  // METHOD TO SWITCH LANGUAGE
  switchLanguage(questionId: number): void {
    // Mark that user has manually switched language
    this.hasUserSwitchedLanguage = true;
    this.currentLanguageId = questionId;
    
    // Get details for selected language
    if (this.allQuestionDetails[questionId]) {
      // For practical/viva questions, preserve question-specific details across languages
      const currentEvaluationMode = this.data.details?.['Evaluation Mode']?.toLowerCase() || '';
      const isPracticalOrViva = currentEvaluationMode === 'practical' || currentEvaluationMode === 'viva';
      
      // Store question-specific details if practical/viva
      const preservedQuestionSpecific = isPracticalOrViva ? {
        'Options': this.data.details?.['Options'],
        'Correct Answer': this.data.details?.['Correct Answer'],
        'Statement': this.data.details?.['Statement'],
        'Blanks': this.data.details?.['Blanks'],
        'Answer Key': this.data.details?.['Answer Key'],
        'Total Blanks': this.data.details?.['Total Blanks'],
        'Matching Table': this.data.details?.['Matching Table'],
        'Correct Options': this.data.details?.['Correct Options'],
        'All Options': this.data.details?.['All Options'],
        'Sample Answer': this.data.details?.['Sample Answer'],
        'Answer Keywords': this.data.details?.['Answer Keywords'],
        'Evaluation Remarks': this.data.details?.['Evaluation Remarks'],
        'KPI Details': this.data.details?.['KPI Details'],
        'KPI Records': this.data.details?.['KPI Records']
      } : null;
      
      // Update with new language details (deep copy to avoid reference issues)
      this.data.details = JSON.parse(JSON.stringify(this.allQuestionDetails[questionId]));
      
      // Restore preserved question-specific details if practical/viva
      if (isPracticalOrViva && preservedQuestionSpecific) {
        Object.entries(preservedQuestionSpecific).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            this.data.details[key] = value;
          }
        });
      }
      
      // Clear previous data and re-categorize
      this.basicInfo = [];
      this.courseInfo = [];
      this.questionSpecificInfo = [];
      this.additionalInfo = [];
      this.filesInfo = [];
      this.statusInfo = [];
      
      // Re-categorize with new language details (but with preserved question-specific details for practical/viva)
      this.categorizeDetails();
      
      // Ensure change detection runs immediately
      this.cdr.detectChanges();
    }
  }

  // TrackBy function to prevent re-rendering on language tabs
  trackByQuestionId(index: number, question: any): number {
    return question.question_id;
  }

  // Helper method to strip HTML tags
  private stripHtmlTags(html: string): string {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  closeDialog() {
    this.dialogRef.close();
  }

  isFullWidth(key: string): boolean {
    const fullWidthKeys = [
      'Options',
      'Primary List',
      'Secondary List',
      'KPI Details',
      'KPI Records',
      'Question Text',
      'Statement',
      'Blanks',
      'Answer Key',
      'Matching Table'
    ];
    return fullWidthKeys.includes(key);
  }

  // Helper method to get latest matching table options
  private getLatestMatchingTableOptions(options: any[]): any[] {
    if (!options || options.length === 0) return options;
    
    // Group options by list_order_id and is_primary_list, keep only the latest (highest ID) for each group
    const groupedOptions: { [key: string]: any } = {};
    
    options.forEach((option: any) => {
      const groupKey = `${option.is_primary_list}_${option.list_order_id}`;
      
      // Keep the option with the highest ID (latest update)
      if (!groupedOptions[groupKey] || option.id > groupedOptions[groupKey].id) {
        groupedOptions[groupKey] = option;
      }
    });
    
    // Convert grouped options back to array
    return Object.values(groupedOptions);
  }

  categorizeDetails() {
    const details = this.data.details || {};
    
    // Get full name or username from localStorage
    const nameFromStorage = localStorage.getItem('full_name') || localStorage.getItem('fullname') || localStorage.getItem('username') || 'Unknown';
    
    // Replace "system" or empty created_user_username with actual name
    if (!details.created_user_username || details.created_user_username === 'system' || details.created_user_username === '') {
      details.created_user_username = nameFromStorage;
    }
    
    // Add enriched labels from element to details if available
    if (this.element?.enrichedLabelsForDisplay && this.element.enrichedLabelsForDisplay.length > 0) {
      details.Labels = JSON.stringify({
        isCollapsibleLabels: true,
        data: this.element.enrichedLabelsForDisplay
      });
      console.log('✅ Added enriched labels to details:', this.element.enrichedLabelsForDisplay);
    }
    
    // If question has been modified and is a matching question, filter to show only recent options
    if (details.modified_datetime && details.question_options && details.question_type_name === 'Matching Questions') {
      details.question_options = this.getLatestMatchingTableOptions(details.question_options);
    }
    
    // Define categories
    const categories = {
      basic: ['Question ID', 'Question Text', 'Question Type', 'Difficulty Level', 'Evaluation Mode', 'Language'],
      course: ['Course/Job Role', 'Course Code', 'Version'],
      questionSpecific: [
        'Options', 'Correct Answer', 'Statement', 'Blanks', 'Answer Key', 'Total Blanks',
        'Matching Table', 'Correct Options', 'All Options',
        'Sample Answer', 'Answer Keywords', 'Evaluation Remarks', 'KPI Details', 'KPI Records'
      ],
      additional: ['Additional Information', 'Question Hint', 'Question Feedback', 'Question Remarks'],
      files: ['Attachments', 'Labels'],
      status: ['Status', 'Archived', 'Created Date', 'Modified Date', 'Created By'],
      hidden: ['created_user_username', 'created_user_name', 'created_user'] // Hide the raw API fields
    };

    // Fields that should have HTML tags removed and displayed as plain text
    const fieldsToStripHtml = ['Question Hint', 'Additional Information', 'Statement', 'Question Feedback', 'Question Remarks'];

    // Helper function to process value
    const processValue = (key: string, value: any): SafeHtml => {
      if (!value || value === 'None' || value === 'N/A' || value === 'Not available') {
        return this.sanitizer.bypassSecurityTrustHtml(
          '<span style="color: #999999; font-style: italic;">Not available</span>'
        );
      }
      
      // For Created Date and Modified Date, show only the date part (no time)
      if (key === 'Created Date' || key === 'Modified Date') {
        const dateString = value.toString();
        // Extract only the date part (first 11 characters like "30 Dec 2025")
        const dateOnly = dateString.split(' ').slice(0, 3).join(' ');
        return this.sanitizer.bypassSecurityTrustHtml(dateOnly);
      }
      
      // For Evaluation Mode, capitalize first letter
      if (key === 'Evaluation Mode') {
        const text = value.toString();
        const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        return this.sanitizer.bypassSecurityTrustHtml(capitalizedText);
      }
      
      // For fields that should have HTML stripped
      if (fieldsToStripHtml.includes(key)) {
        const strippedText = this.stripHtmlTags(value.toString());
        const safeText = strippedText
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>')
          .trim();
        return this.sanitizer.bypassSecurityTrustHtml(safeText);
      }
      
      // For question text, preserve formatting
      if (key === 'Question Text') {
        const formattedText = value.toString()
          .replace(/\n/g, '<br>')
          .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
        return this.sanitizer.bypassSecurityTrustHtml(formattedText);
      }
      
      // For matching table and other HTML content
      if (key === 'Matching Table' || key === 'Options' || key === 'KPI Details' || key === 'KPI Records' || key === 'All Options' || key === 'Correct Options' || key === 'Sample Answer' || key === 'Answer Keywords' || key === 'Evaluation Remarks' || key === 'Initial Text') {        
          return this.sanitizer.bypassSecurityTrustHtml(value);
      }
      
      // For attachments with HTML links
      if (key === 'Attachments' && value.includes('<a href')) {
        return this.sanitizer.bypassSecurityTrustHtml(value);
      }
      
      // For arrays/lists
      if (typeof value === 'string' && (value.includes('<br>') || value.includes('|'))) {
        return this.sanitizer.bypassSecurityTrustHtml(value);
      }
      
      
      // Default: escape HTML but preserve line breaks
      const safeText = value.toString()
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      return this.sanitizer.bypassSecurityTrustHtml(safeText);
    };

    // Categorize and process each detail
    Object.entries(details).forEach(([key, value]) => {
      // Skip hidden fields
      if (categories.hidden.includes(key)) {
        return;
      }

      // Special handling for Labels field
      if (key === 'Labels' && typeof value === 'string') {
        try {
          const labelData = JSON.parse(value);
          if (labelData.isCollapsibleLabels && labelData.data) {
            const detailItem = {
              key: key,
              label: key,
              safeValue: this.sanitizer.bypassSecurityTrustHtml(''),
              isLabels: true,
              labelsData: labelData.data
            };
            this.filesInfo.push(detailItem);
            return;
          }
        } catch (e) {
          // Not JSON, continue with normal processing
        }
      }

      const detailItem = {
        key: key,
        label: key,
        safeValue: processValue(key, value)
      };

      if (categories.basic.includes(key)) {
        this.basicInfo.push(detailItem);
      } else if (categories.course.includes(key)) {
        this.courseInfo.push(detailItem);
      } else if (categories.questionSpecific.includes(key)) {
        this.questionSpecificInfo.push(detailItem);
      } else if (categories.additional.includes(key)) {
        this.additionalInfo.push(detailItem);
      } else if (categories.files.includes(key)) {
        this.filesInfo.push(detailItem);
      } else if (categories.status.includes(key)) {
        this.statusInfo.push(detailItem);
      } else {
        // Default to additional info for any uncategorized items
        this.additionalInfo.push(detailItem);
      }
    });

    // Sort basic info to have Question ID first
    this.basicInfo.sort((a, b) => {
      if (a.label === 'Question ID') return -1;
      if (b.label === 'Question ID') return 1;
      if (a.label === 'Question Text') return -1;
      if (b.label === 'Question Text') return 1;
      return 0;
    });
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  // Helper method to toggle label expansion by labelId
  toggleLabelExpand(labelId: number): void {
    this.expandedLabels[labelId] = !this.expandedLabels[labelId];
  }

  // Helper method to check if a label has children
  hasChildren(label: any, allLabels: any[]): boolean {
    return allLabels.some(l => l.parentLabelId === label.labelId);
  }

  // Helper method to get direct children of a label
  getDirectChildren(label: any, allLabels: any[]): any[] {
    return allLabels.filter(l => l.parentLabelId === label.labelId);
  }

  // Helper method to get full label title showing both API name and original name
  getFullLabelTitle(label: any): string {
    if (label.apiLabelName && label.apiLabelName !== label.labelName) {
      return `${label.apiLabelName} (Original: ${label.labelName})`;
    }
    return label.labelName || '';
  }
}